/**
 * useLongPress — fires `onLongPress` after `delay` ms of continuous touch/click.
 * Cancels if the pointer moves > 8px (treats it as a scroll) or is released early.
 *
 * Why a separate hook from useLongPressDrag?
 *   useLongPressDrag is for drag-and-drop on the calendar. This hook just
 *   detects a hold-and-open-menu gesture — the most common iOS pattern.
 */
import { useCallback, useRef } from "react";

export function useLongPress(onLongPress, { delay = 450 } = {}) {
  const timerRef = useRef(null);
  const startRef = useRef({ x: 0, y: 0 });
  const firedRef = useRef(false);

  const clear = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const onStart = useCallback((e) => {
    firedRef.current = false;
    const pt = e.touches ? e.touches[0] : e;
    startRef.current = { x: pt.clientX, y: pt.clientY };
    clear();
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      try { if (navigator.vibrate) navigator.vibrate(12); } catch { /* noop */ }
      onLongPress(e);
    }, delay);
  }, [onLongPress, delay]);

  const onMove = useCallback((e) => {
    if (!timerRef.current) return;
    const pt = e.touches ? e.touches[0] : e;
    const dx = Math.abs(pt.clientX - startRef.current.x);
    const dy = Math.abs(pt.clientY - startRef.current.y);
    if (dx > 8 || dy > 8) clear();
  }, []);

  const onEnd = useCallback(() => {
    clear();
  }, []);

  return {
    bind: {
      onTouchStart: onStart,
      onTouchMove: onMove,
      onTouchEnd: onEnd,
      onTouchCancel: onEnd,
      onMouseDown: onStart,
      onMouseMove: onMove,
      onMouseUp: onEnd,
      onMouseLeave: onEnd,
      onContextMenu: (e) => {
        // Right-click / Android context menu also opens the action sheet.
        e.preventDefault();
        if (!firedRef.current) {
          firedRef.current = true;
          onLongPress(e);
        }
      },
    },
    didFire: () => firedRef.current,
  };
}
